/* eslint-disable max-classes-per-file */

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'

const BINARY_EXTENSION_HEADER_MAGIC = 'glTF'
const BINARY_EXTENSION_HEADER_LENGTH = 12
const BINARY_EXTENSION_CHUNK_JSON = 0x4E4F534A

const meshCompressionNone = 'none'
const meshCompressionDraco = 'draco'
const textureCompressionNone = 'none'
const textureCompressionBasisU = 'basisU'

export const errorDuringFileLoad = 'errorDuringFileLoad'
export const errorDuringGltfJsonAnalyze = 'errorDuringGltfJsonAnalyze'
export const errorDuringGltfInit = 'errorDuringGltfInit'

class GLTFTextureBasisUExtension {
    constructor(parser, metrics) {
        this.parser = parser
        this.name = 'KHR_texture_basisu'
        this.metrics = metrics
    }

    loadTexture (textureIndex) {
        const parser = this.parser
        const json = parser.json

        const textureDef = json.textures[textureIndex]

        if ( ! textureDef.extensions || !textureDef.extensions[this.name]) {
            return null
        }

        const extension = textureDef.extensions[this.name]
        const source = json.images[extension.source]
        const loader = parser.options.ktx2Loader

        if (!loader) {
            if (json.extensionsRequired && json.extensionsRequired.indexOf(this.name) >= 0) {
                throw new Error( 'THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures' )
            } else {
                // Assumes that the extension is optional and that a fallback texture is present
                return null
            }
        }

        if (this.metrics) {
            this.metrics.TextureCompression = textureCompressionBasisU
        }
        return parser.loadTextureImage(textureIndex, source, loader)
            .then((texture) => {
                /* eslint-disable no-param-reassign */
                texture.magFilter = THREE.LinearFilter
                texture.minFilter = THREE.LinearFilter
                /* eslint-enable no-param-reassign */
                if (loader.transcodingTimes.has(texture.uuid) && this.metrics) {
                    this.metrics.msBasisUniversalTextureTranscodingTime += loader.transcodingTimes.get(texture.uuid)
                }
                return texture
            })
    }
}

class CustomGLTFLoader extends GLTFLoader {
    load (url, onLoad, onProgress, onError, metrics) {
        let resourcePath = null

        if (this.resourcePath !== '') {
            resourcePath = this.resourcePath
        } else if (this.path !== '') {
            resourcePath = this.path
        } else {
            resourcePath = THREE.LoaderUtils.extractUrlBase(url)
        }
    
        // Tells the LoadingManager to track an extra item, which resolves after
        // the model is fully loaded. This means the count of items loaded will
        // be incorrect, but ensures manager.onLoad() does not fire early.
        this.manager.itemStart(url)
        const onErrorWrapped = (e) => {
            if (onError) {
                onError(e)
            } else {
                // console.error(e)
            }
            this.manager.itemError(url)
            this.manager.itemEnd(url)
        }
    
        const loader = new THREE.FileLoader(this.manager)
    
        loader.setPath(this.path)
        loader.setResponseType('arraybuffer')
        loader.setRequestHeader(this.requestHeader)
        loader.setWithCredentials(this.withCredentials)
    
        const downloadStart = window.performance.now()
        loader.load( url, (data) => {
            if (metrics) {
                /* eslint-disable no-param-reassign */
                metrics.sFileDownloadTime = (window.performance.now() - downloadStart) / 1000
                metrics.TextureCompression = textureCompressionNone
                metrics.MbFileSizeTransferred = data.byteLength / 1024 / 1024
                /* eslint-enable no-param-reassign */
            }

            try {
                const gltfInfo = CustomGLTFLoader.analyzeJson(data)

                // this function will be called in the beginning of this.parse, synchronously,before
                // starting any async operation, i.e. metrics variable will stay unchanged until then
                const khrTextureBasisuIdx = 2
                this.pluginCallbacks[khrTextureBasisuIdx] = (parser) =>
                    new GLTFTextureBasisUExtension(parser, metrics)

                this.parse(data, resourcePath, (gltf) => {
                    if (metrics) {
                        /* eslint-disable-next-line no-param-reassign */
                        metrics.MeshCompression = gltfInfo.extensionsUsed.has('KHR_draco_mesh_compression') ?
                            meshCompressionDraco :
                            meshCompressionNone
                    }
                    onLoad(gltf)
                    this.manager.itemEnd(url)
                }, (event) => onErrorWrapped({
                    type: errorDuringGltfInit,
                    event,
                }), metrics )
            } catch (e) {
                onErrorWrapped({
                    type: errorDuringGltfJsonAnalyze,
                    event: e
                })
            }
        }, onProgress, (e) => onErrorWrapped({
            type: errorDuringFileLoad,
            event: e
        }))
    }

    static analyzeJson (data) {
        const gltfInfo = {
            extensionsUsed: new Set()
        }

        let content

        if (typeof data === 'string') {
            content = data
        } else {
            const magic = THREE.LoaderUtils.decodeText(new Uint8Array(data, 0, 4))
            if (magic === BINARY_EXTENSION_HEADER_MAGIC) {
                const headerView = new DataView(data, 0, BINARY_EXTENSION_HEADER_LENGTH)
                const header = {
                    magic: THREE.LoaderUtils.decodeText(new Uint8Array(data.slice(0, 4))),
                    version: headerView.getUint32(4, true),
                    length: headerView.getUint32(8, true)
                }
                if (header.magic !== BINARY_EXTENSION_HEADER_MAGIC) {
                    throw new Error('Unsupported glTF-Binary header.')
                } else if (header.version < 2.0) {
                    throw new Error( 'Legacy binary file detected.')
                }
                const chunkView = new DataView(data, BINARY_EXTENSION_HEADER_LENGTH)
                let chunkIndex = 0
                while (chunkIndex < chunkView.byteLength) {
                    const chunkLength = chunkView.getUint32(chunkIndex, true)
                    chunkIndex += 4
                    const chunkType = chunkView.getUint32(chunkIndex, true)
                    chunkIndex += 4
                    if (chunkType === BINARY_EXTENSION_CHUNK_JSON) {
                        const contentArray = new Uint8Array(data,
                            BINARY_EXTENSION_HEADER_LENGTH + chunkIndex, chunkLength)
                        content = THREE.LoaderUtils.decodeText(contentArray)
                    }
                    // Clients must ignore chunks with unknown types.
                    chunkIndex += chunkLength
                }
                if (content === null) {
                    throw new Error( 'THREE.GLTFLoader: JSON content not found.')
                }
            } else {
                content = THREE.LoaderUtils.decodeText(new Uint8Array(data))
            }
        }
        const json = JSON.parse(content)
        if (json.extensionsUsed) {
            json.extensionsUsed.forEach(extensionName => gltfInfo.extensionsUsed.add(extensionName))
        }
        return gltfInfo
    }
}

// const gltfLoader = new CustomGLTFLoader()
// gltfLoader.setKTX2Loader(new CustomKTX2Loader())

export default CustomGLTFLoader
